// material
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  Box,
  CircularProgress,
  Button,
  LinearProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import { isEmpty, omitBy } from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import { useMemo, useState } from 'react';

// components
import IccidRangeDialog from '../components/Dialogs/IccidRangeDialog';
import IccidHubDialog from '../components/Dialogs/IccidHubDialog';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import { UserListHead } from '../sections/@dashboard/user';
import { Search, SearchIconWrapper, StyledInputBase } from '../components/SearchInput';

// store
import { api as stockApi, hooks as stockHooks } from '../store/domains/stock';

const TABLE_HEAD = [
  { id: 'iccid', label: 'iccid', alignRight: false },
  { id: 'cmodelNameompany', label: 'modelName', alignRight: false },
  { id: 'partnerId', label: 'Partner', alignRight: true },
  { id: 'hubId', label: 'Hub', alignRight: true },
  { id: '' },
];

export default function IccId() {
  const {
    handleChangeRowsPerPage,
    handleClick,
    handleCloseRangeDialog,
    handleOpenRangeDialog,
    resetSelectedList,
    handleCloseHubDialog,
    handleOpenHubDialog,
    handleChangeHub,
    hub,
    rowsPerPage,
    selected,
    openRangeDialog,
    openHubDialog,
    isTransfer,
    isUnassign,
  } = stockHooks.useStock();
  const [searchValue, setSearchValue] = useState('');
  const [partnerValue, setPartnerValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const params = useMemo(
    () => omitBy({ size: rowsPerPage, iccid: searchValue }, (v) => v.toString().length < 1),
    [rowsPerPage, searchValue]
  );

  const [postIccid, { isLoading: loadingPostIccid }] = stockApi.usePostIccid();
  const { data: iccidData, isLoading: iccidDataLoading, isFetching } = stockApi.useGetIccid(params);
  const { data: posManagerOrganizations, isLoading: posManagerOrganizationsLoading } =
    stockApi.useGetPosManagerOrganizations(hub, {
      skip: !hub,
    });

  const { data: hubList, isLoading: hubListLoading } = stockApi.useGetHubList();

  const handleChange = (value) => {
    setPartnerValue(value.label);
    postIccid({ partnerId: value.id, identifiers: selected })
      .unwrap()
      .then(() => {
        resetSelectedList();
        setPartnerValue('');
      });
  };
  const handleChangeSearchInput = (event) => {
    setSearchValue(event.target.value);
  };
  if (iccidDataLoading || posManagerOrganizationsLoading || hubListLoading) {
    return <CircularProgress />;
  }

  return (
    <Page title="User">
      <IccidRangeDialog
        isUnassign={isUnassign}
        isTransfer={isTransfer}
        open={openRangeDialog}
        handleClose={handleCloseRangeDialog}
      />
      <IccidHubDialog open={openHubDialog} handleClose={handleCloseHubDialog} hubList={hubList} />
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack spacing={3} sx={{ my: 2 }}>
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                onChange={handleChangeSearchInput}
                value={searchValue}
                placeholder="Search…"
                inputProps={{ 'aria-label': 'search' }}
              />
            </Search>
          </Stack>
          <Box display="flex" spacing={3} sx={{ my: 2 }}>
            <Autocomplete
              value={partnerValue}
              onChange={(_, newValue) => {
                handleChange(newValue);
              }}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              options={posManagerOrganizations || []}
              sx={{ width: 300 }}
              renderInput={(params) => <TextField {...params} label="Select Partner" />}
              disabled={isEmpty(selected)}
            />
            <FormControl
              sx={{
                mx: 2,
                width: 130,
              }}
            >
              <InputLabel>Rows per page:</InputLabel>
              <Select value={rowsPerPage} label="Rows per page:" onChange={handleChangeRowsPerPage}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Button onClick={() => handleOpenRangeDialog({ isUnassign: true })} variant="contained">
              Unassign dealer
            </Button>
            <Button sx={{ ml: 1 }} onClick={handleOpenRangeDialog} variant="contained">
              Assign to dealer
            </Button>
            <Button sx={{ ml: 1 }} onClick={() => handleOpenRangeDialog({ isTransfer: true })} variant="contained">
              Transfer dealer
            </Button>
            <Button sx={{ ml: 1 }} onClick={handleOpenHubDialog} variant="contained">
              Set hub
            </Button>
          </Box>
        </Stack>
        <Card>
          {(loadingPostIccid || isFetching) && <LinearProgress />}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead headLabel={TABLE_HEAD} rowCount={iccidData?.data?.length} numSelected={selected.length} />
                <TableBody>
                  {iccidData?.data?.map((row) => {
                    const { iccid, modelName, partnerName, hubName, hubId } = row;
                    const isItemSelected = selected.indexOf(iccid) !== -1;

                    return (
                      <TableRow hover key={iccid} tabIndex={-1} selected={isItemSelected} aria-checked={isItemSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            disabled={!hubName}
                            checked={isItemSelected}
                            onChange={(event) => {
                              handleClick(event, iccid);
                              handleChangeHub(hubId);
                            }}
                          />
                        </TableCell>
                        <TableCell component="th" scope="row">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2" noWrap>
                              {iccid}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="left">{modelName}</TableCell>
                        <TableCell align="right">{partnerName}</TableCell>
                        <TableCell align="right">{hubName}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>
    </Page>
  );
}
